import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useLocalizedNavigate } from '@/hooks/useLocalizedNavigate';
import { supabase } from '@/integrations/supabase/client';
import { BoardGrafik, wedge } from '@/components/BoardTrefferbild';

/**
 * Einmaliger Hinweis auf das neue Trefferbild.
 *
 * Erscheint nur bei Konten, die vor der Einführung bestanden haben: dort ist
 * profiles.trefferbild_notice_seen_at NULL. Neue Konten bekommen den Zeitstempel
 * automatisch als Vorgabewert und sehen den Hinweis deshalb nie.
 *
 * Nur Persönliches Training. Liga, Challenges und Online-Games sind nicht betroffen.
 */

/** Seiten, auf denen der Hinweis nicht erscheint. Er bleibt offen und kommt auf der naechsten normalen Seite. */
const OHNE_HINWEIS = [
  '/',
  '/impressum',
  '/datenschutz',
  '/nutzungsbedingungen',
  '/agb',
  '/kontakt',
  '/faq',
  '/ueber-uns',
  '/darts-training',
  '/widerruf',
  '/vertrag-widerrufen',
  '/pro',
  '/reset-password',
];

const R_DI = 219, R_DO = 230;

/** Beispielpfeile auf die D20, nur zur Veranschaulichung. Keine Werte des Spielers. */
const BEISPIEL = '261,78 285,105 269,85 284,60 273,94 286,105 283,60 280,78 281,107 277,100 283,85 262,72 289,72 275,91 285,75 272,81 293,78 280,58 276,78 296,78 287,56 276,53 299,71 283,51 269,66 283,78 279,109 287,65 287,60 267,55 266,56 289,56'
  .split(' ')
  .map((v) => {
    const [x, y] = v.split(',').map(Number);
    return { x, y };
  });

export const TrefferbildHinweis: React.FC = () => {
  const navigate = useLocalizedNavigate();
  const { pathname } = useLocation();
  const [offen, setOffen] = useState(false);
  const breit = typeof window !== 'undefined' && window.innerWidth >= 1280;
  const pfad = pathname.replace(/^\/(de|en)(?=\/|$)/, '') || '/';

  useEffect(() => {
    let abgebrochen = false;

    const pruefen = async () => {
      const { data: auth } = await supabase.auth.getUser();
      const uid = auth?.user?.id;
      if (!uid) return;
      const { data, error } = await supabase
        .from('profiles')
        .select('trefferbild_notice_seen_at')
        .eq('user_id', uid)
        .maybeSingle();
      if (abgebrochen || error || !data) return;
      if (!(data as any).trefferbild_notice_seen_at) setOffen(true);
    };

    pruefen();

    // Auch pruefen, wenn sich jemand erst waehrend der laufenden Sitzung anmeldet.
    // Der Aufruf wird verzoegert, weil im Rueckruf keine weiteren Supabase-Aufrufe
    // direkt erfolgen duerfen.
    let zeitgeber: ReturnType<typeof setTimeout> | undefined;
    const { data: sub } = supabase.auth.onAuthStateChange((_ereignis, session) => {
      if (!session?.user) return;
      zeitgeber = setTimeout(() => {
        if (!abgebrochen) pruefen();
      }, 0);
    });

    return () => {
      abgebrochen = true;
      if (zeitgeber) clearTimeout(zeitgeber);
      sub?.subscription?.unsubscribe();
    };
  }, []);


  if (!offen || OHNE_HINWEIS.includes(pfad)) return null;

  const schliessen = async (zurStatistik: boolean) => {
    setOffen(false);
    const { data: auth } = await supabase.auth.getUser();
    const uid = auth?.user?.id;
    if (uid) {
      await supabase
        .from('profiles')
        .update({ trefferbild_notice_seen_at: new Date().toISOString() } as any)
        .eq('user_id', uid);
    }
    if (zurStatistik) navigate('/statistics?tab=training');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/65 p-4 backdrop-blur-sm">
      <div className="w-full max-w-md rounded-2xl border border-border bg-card p-5 shadow-2xl">
        <span className="inline-block rounded-lg bg-primary/15 px-2.5 py-1.5 text-[10.5px] font-extrabold uppercase tracking-[1.6px] text-primary">
          Neu im Persönlichen Training
        </span>

        <h2 className="mt-3 text-2xl font-extrabold tracking-tight text-foreground">Dein Trefferbild</h2>

        <div className="mt-2 inline-block rounded-lg border border-primary/30 bg-primary/10 px-2.5 py-1.5 text-xs font-semibold text-primary">
          Statistik <span className="mx-1 opacity-60">›</span> Persönliches Training
        </div>

        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
          Du siehst jetzt, wo deine Pfeile rund um jedes Ziel landen. Wähl ein Doppel, ein Triple oder eine ganze
          Zahl, und du siehst deine Treffer, deine Quote und in welche Richtung du im Schnitt danebenliegst.
        </p>

        <div className="relative mt-3 flex justify-center rounded-xl border border-border bg-[#04060a] p-2.5">
          <svg viewBox="150 20 260 260" xmlns="http://www.w3.org/2000/svg" className="block h-auto w-full max-w-[210px]">
            <BoardGrafik />
            <path d={wedge(R_DI, R_DO, -9, 9)} fill="rgba(52,210,122,.30)" stroke="#34d27a" strokeWidth={2.5} />
            {BEISPIEL.map((p, i) => (
              <circle key={i} cx={p.x} cy={p.y} r={4} fill="#e9eff6" stroke="#04060a" strokeWidth={1.2} />
            ))}
          </svg>
          <span className="absolute bottom-3 right-3 text-[10px] text-muted-foreground">Beispielbild</span>
        </div>

        <div className="mt-3 rounded-xl border border-border bg-muted/40 px-3 py-2.5 text-xs leading-relaxed text-muted-foreground">
          {breit
            ? 'Die Punkte entstehen, wenn du deine Pfeile direkt am Dartboard antippst statt über die Knöpfe. Spiel eine Einheit so, dann füllt sich dein Trefferbild.'
            : 'Die Punkte entstehen nur, wenn du deine Pfeile direkt am Dartboard antippst. Diese Eingabe gibt es am Computer ab einer Fensterbreite von 1280 Pixeln. Am Handy siehst du das Trefferbild, sobald du einmal am Computer trainiert hast.'}
        </div>

        <div className="mt-4 flex gap-2.5">
          <button
            onClick={() => schliessen(true)}
            className="flex-1 rounded-xl bg-primary px-4 py-3 text-sm font-bold text-primary-foreground"
          >
            Zur Statistik
          </button>
          <button
            onClick={() => schliessen(false)}
            className="flex-1 rounded-xl border border-border px-4 py-3 text-sm font-bold text-muted-foreground"
          >
            Später
          </button>
        </div>
      </div>
    </div>
  );
};

export default TrefferbildHinweis;
